import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { BatchStatusChip } from 'web-components-ts';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { isNaN } from 'lodash';
import { ROUTE_BATCH_RECORDS } from '../../attrs/routePaths';
import { getToFixedValue } from '../../helpers/utils';

const CardStyle = styled(Card)`
  &:hover {
    cursor: pointer;
    box-shadow: 0 0 5px 1px lightgray;
  }

  width: 362px;
  height: 202px;
`;

type BatchStatus = ComponentProps<typeof BatchStatusChip>['status'];

interface BatchCardProps {
  batch: {
    batch_id: string;
    end_time: string;
    grinding_energy: number;
    id: string;
    machine: string;
    origin: string;
    progress: number;
    start_time: string;
    status: BatchStatus;
    recipe?: string;
  };
}

const BatchCard = ({ batch }: BatchCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const batchProgress = getToFixedValue(batch.progress || 0, 0);

  const handleSelectBatch = (batchId: string) => history.push(`/${ROUTE_BATCH_RECORDS}/${batchId}`);

  return (
    <CardStyle onClick={() => handleSelectBatch(batch.id)} style={{ padding: '1em', margin: '0.5em', display: 'grid' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" style={{ fontWeight: '800' }}>
            {batch.batch_id}
          </Typography>
          <Typography variant="subtitle2">{batch.recipe ? batch.recipe : '-'}</Typography>
        </Box>

        <Box sx={{ fontSize: '0.5rem !important' }}>
          <BatchStatusChip status={batch.status} label={t(`batch_records.overview.${batch.status?.toLowerCase()}`)} />
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2">{batchProgress}%</Typography>
        <LinearProgress
          sx={{
            height: '8px',
            mb: 2,
            background: '#a5c7ea'
          }}
          color="info"
          variant="determinate"
          value={batchProgress > 100 ? 100 : batchProgress}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Stack direction="column">
            <Typography variant="subtitle2" sx={{ color: 'gray', fontWeight: '500' }}>
              {t('batch_records.batches_in_operation.machine_name')}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: '500', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {batch.machine}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" sx={{ color: 'gray', fontWeight: '500' }}>
            {t('batch_records.batches_in_operation.grinding_energy')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: '500' }}>
            {isNaN(Number(batch.grinding_energy)) ? '-' : `${Math.round(batch.grinding_energy * 10) / 10}kWh`}
          </Typography>
        </Grid>
      </Grid>
    </CardStyle>
  );
};

export default BatchCard;
